import React, { useContext, useEffect, useState } from 'react'

import {
  COLUMN_NAMES as DUEOUT_COLUNN_NAMES,
  DueOutFetchArgs,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE as DUEOUT_STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  InquiryFetchArgs,
} from '../../assets/ts/data/inquiry/inquiry'
import { STATUS_CODE as INQUIRY_STATUS_CODE } from '../../assets/ts/data/inquiry/inquiry-status-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import Announcements from '../../components/announcements/announcement'
import HomeCalendar from '../../components/calendar/calendar-items'
import KnowledgeBase from '../../components/knowledge-base/knowledge-base'
import Tabs, { TabItem } from '../../components/tabs/tabs'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import PageHeader from '../../layouts/header/page-header'
import { MetadataContext } from '../../store/metadata-context-provider'
import { SelectionContextProvider } from '../../store/selection-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
} from '../../utils/constants/global-constants'
import { optionsTypes } from '../../utils/controls/select-options-data'
import CustomerDashboardDueouts from './customer-dashboard-dueouts'
import CustomerDashboardInquiries from './customer-dashboard-inquires'
import './home.css'

interface Customerprops {
  CategoryOptions: optionsTypes[]
  SectionOptions: optionsTypes[]
}

const INQUIRIES_TAB = 0
const DUEOUTS_TAB = 1

const CustomerView = (props: Customerprops) => {
  const { CategoryOptions, SectionOptions } = props
  const {
    setCategoryValue,
    setSectionValue 
  } = useContext(MetadataContext)
  const userContext = useContext(UserContext)
  const currentUser = userContext.userId
  const [tabSelected, setTabSelected] = useState(INQUIRIES_TAB)
  const [searchValue, setSearchValue] = useState('')

  const handleTabSelection = (tabNumber) => {
    setTabSelected(tabNumber)
  }

  const initialInquiryFilter: InquiryFetchArgs = {
    assignedComponentCodeFilter: ALL_SELECT_VALUE,
    currentlyServicingByFilter : ALL_SELECT_VALUE_STRING,
    inquiryTypeFilter : ALL_SELECT_VALUE_STRING,
    isArchivedFilter: false,
    isAscending: false,
    isSavedInquiryDeleted : false,
    orderBy: INQUIRY_COLUMN_NAMES.ItemModified,
    otherFilter: null,
    relatedUserFilter: currentUser,
    sectionCodeFilter: ALL_SELECT_VALUE,
    selfFilter: null,
    servicingByFilter: ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
    servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    yearFilter: ALL_SELECT_VALUE,
  }

  const initialDueOutFilter: DueOutFetchArgs = {
    assignedComponentCodeFilter: ALL_SELECT_VALUE,
    isArchivedFilter: false,
    isAscending: false,
    orderBy: DUEOUT_COLUNN_NAMES.ItemModified,
    relatedUserFilter: currentUser,
    sectionCodeFilter: ALL_SELECT_VALUE,
    servicingByFilter: ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
    servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    statusCodesOmittedFilter: [DUEOUT_STATUS_CODE.SAVED],
    yearFilter: ALL_SELECT_VALUE,
  }

  const [inquiryFilter, setInquiryFilter] = useState(initialInquiryFilter)
  const [dueoutFilter, setDueoutFilter] = useState(initialDueOutFilter)

  const resetFilterOptions = (priorArgs, order, updatedArgs) => ({
    ...priorArgs,
    assignedComponentCodeFilter:
      order && order >= 3 ? priorArgs.assignedComponentCodeFilter : ALL_SELECT_VALUE,
    currentlyServicingByFilter:
      order && order >= 5 ? priorArgs.currentlyServicingByFilter : ALL_SELECT_VALUE_STRING,
    inquiryTypeFilter: order && order >= 2 ? priorArgs.inquiryTypeFilter : ALL_SELECT_VALUE_STRING,
    sectionCodeFilter: order && order >= 7 ? priorArgs.sectionCodeFilter : ALL_SELECT_VALUE,
    servicingByFilter: order && order >= 5 ? priorArgs.servicingByFilter : ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter:
      order && order >= 4 ? priorArgs.servicingMAJCOMCodeFilter : ALL_SELECT_VALUE,
    servicingOrganizationFilter:
      order && order >= 6 ? priorArgs.servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    yearFilter: order && order >= 1 ? priorArgs.yearFilter : ALL_SELECT_VALUE,
    ...updatedArgs,
    relatedUserFilter: currentUser,
  })

  const updateInquiryFilter = (updatedArgs, order) => {
    setInquiryFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateDueOutFilter = (updatedArgs , order) => {
    setDueoutFilter((priorArgs) => resetFilterOptions(priorArgs, order, updatedArgs))
  }

  const { inquiryCountValue } = useInquiryCount({
    ...inquiryFilter,
    // Although the overall dashboard inquiry filter includes
    // SAVED items (so that the SAVED KPI, for example, will function)
    // we want to omit SAVED items from the overall tab count
    // This is a little inconsistent, but matches how the existing MPF
    // product works...
    statusCodesOmittedFilter:[INQUIRY_STATUS_CODE.SAVED]
  })
  const inquiryCountString = inquiryCountValue.toLocaleString()

  const { dueOutCountValue } = useDueOutCount(dueoutFilter)
  const dueOutCountString = dueOutCountValue.toLocaleString()

  const handleClearAll = () => {
    setInquiryFilter(initialInquiryFilter)
    setDueoutFilter(initialDueOutFilter)
  }
  useEffect(()=>{
    handleClearAll()
  },[tabSelected])

  const customertabs: TabItem[] = [
    {
      content: (
        <SelectionContextProvider>
          <CustomerDashboardInquiries
            filter={inquiryFilter}
            isSavedInquiryDeleted = {inquiryFilter.isSavedInquiryDeleted as boolean}
            onClearAll={handleClearAll}
            onUpdateFilter={updateInquiryFilter}
            tabSelected={tabSelected}
          />
        </SelectionContextProvider>
      ),
      count: twoDigits(inquiryCountString),
      isActive: tabSelected === INQUIRIES_TAB,
      tabTitle: 'Inquiries',
    },

    {
      content: (
        <SelectionContextProvider>
          <CustomerDashboardDueouts
            filter={dueoutFilter}
            onClearAll={handleClearAll}
            onUpdateFilter={updateDueOutFilter}
            tabSelected={tabSelected}
          />
        </SelectionContextProvider>
      ),
      count: twoDigits(dueOutCountString),
      isActive: tabSelected === DUEOUTS_TAB,
      tabTitle: 'Due Outs',
    },
  ]

  return (
    <div className='customer-view mb-3'>
      <PageHeader
        icon='icon-dashboard2 font-14 me-1 color-primary'
        name='Dashboard'
      />
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 col-xl-9 mt-0 customerviewl-content'>
            <div className='row'>
              <div className='col-sm-12 d-flex align-items-stretch'>
                <div className='shadow card w-100'>
                  <div className='p-2'>
                    <Tabs
                      anchorclass='font-12 poppins-semibold'
                      badge='badge border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'
                      className=' lg'
                      items={customertabs}
                      onTabSelected={handleTabSelection}
                      tablistclassName='overflow-auto'
                    />
                  </div>
                </div>
              </div>
              <div className='col-md-12 col-xxl-6 d-flex align-items-stretch'>
                <div className='shadow card w-100'>
                  <HomeCalendar 
                    CategoryOptions={[]} 
                    SectionOptions={SectionOptions}
                    isFilters={true}
                    setCategoryValue={setCategoryValue} 
                    setSectionValue={setSectionValue}                  
                  />
                </div>
              </div>
              <div className='col-md-12 col-xxl-6 d-flex align-items-stretch'>
                <div className='shadow card w-100'>
                  <Announcements
                    CategoryOptions={CategoryOptions}
                    SectionOptions={SectionOptions}
                    isFilters
                    setCategoryValue={setCategoryValue} 
                    setSectionValue={setSectionValue}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='col-sm-12 col-xl-3 d-flex align-items-stretch customerviewr-content '>
            <div className='shadow card w-100 kb'>
              <KnowledgeBase
                CategoryOptions={CategoryOptions}
                SectionOptions={SectionOptions}
                isFilters
                searchValue={searchValue} 
                setCategoryValue={setCategoryValue}
                setSearchValue={setSearchValue}
                setSectionValue={setSectionValue}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default CustomerView
